'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import {
    MdAdd,
    MdCheckCircle,
    MdContentCopy,
    MdErrorOutline,
    MdLink,
    MdLinkOff,
    MdRefresh,
    MdSync,
    MdWhatsapp,
} from 'react-icons/md'
import { toast } from 'react-toastify'

import ConfirmModal from '@/components/confirm-modal'
import {
    WhatsappAccountStatusResult,
    WhatsappPhoneNumber,
    WhatsappSignupConfig,
    connectWhatsappAccount,
    disconnectWhatsappAccount,
    getWhatsappAccountStatus,
    getWhatsappSignupConfig,
    registerWhatsappPhoneNumber,
} from '@/lib/api/messaging/whatsapp-account-api'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'

import { useFacebookSdk } from './use-facebook-sdk'

/** O que o popup do Embedded Signup manda por `postMessage` durante o fluxo. */
interface SignupSessionInfo {
    type?: string
    event?: string
    data?: {
        waba_id?: string
        phone_number_id?: string
        current_step?: string
        error_message?: string
    }
}

const QUALITY_COLORS: Record<string, string> = {
    GREEN: 'badge-success',
    YELLOW: 'badge-warning',
    RED: 'badge-error',
}

function StatusBadge({ number }: { number: WhatsappPhoneNumber }) {
    const registered = number.status === 'CONNECTED'
    return (
        <span
            className={`badge badge-sm ${registered ? 'badge-success' : 'badge-ghost'}`}
            title={
                registered
                    ? 'Registrado na Cloud API — já pode enviar'
                    : 'Ainda não registrado: a Meta recusa o envio por este número'
            }
        >
            {registered ? 'Pronto' : (number.status ?? 'Pendente')}
        </span>
    )
}

const WhatsappAccountPage = () => {
    const { canRead, canCreate, canUpdate } = useResourcePermissions(
        'messaging',
        'whatsapp_account'
    )

    const [config, setConfig] = useState<WhatsappSignupConfig | null>(null)
    const [account, setAccount] = useState<WhatsappAccountStatusResult | null>(
        null
    )
    const [loading, setLoading] = useState(true)
    const [connecting, setConnecting] = useState(false)
    const [registeringId, setRegisteringId] = useState<string | null>(null)
    const [showDisconnectModal, setShowDisconnectModal] = useState(false)
    const [disconnecting, setDisconnecting] = useState(false)

    const { ready: sdkReady, failed: sdkFailed } = useFacebookSdk(
        config?.appId,
        config?.graphVersion
    )

    /**
     * O popup manda `waba_id` e `phone_number_id` por `postMessage` ANTES de
     * devolver o `code`. Guardamos numa ref, e não em estado: o `code` chega
     * pelo callback do `FB.login`, que capturaria um estado velho por closure.
     */
    const sessionInfo = useRef<{ wabaId?: string; phoneNumberId?: string }>({})

    const fetchStatus = useCallback(async () => {
        const res = await getWhatsappAccountStatus()
        if (res.success) setAccount(res.result)
        else toast.error(res.error || 'Falha ao consultar a conta')
    }, [])

    useEffect(() => {
        if (!canRead) {
            setLoading(false)
            return
        }
        Promise.all([getWhatsappSignupConfig(), getWhatsappAccountStatus()])
            .then(([cfg, status]) => {
                if (cfg.success) setConfig(cfg.result)
                if (status.success) setAccount(status.result)
            })
            .finally(() => setLoading(false))
    }, [canRead])

    // Escuta o popup. Só aceita mensagem vinda do domínio do Facebook — sem
    // esta checagem, qualquer aba aberta poderia forjar uma conexão.
    useEffect(() => {
        const onMessage = (event: MessageEvent) => {
            if (!event.origin.endsWith('facebook.com')) return

            let payload: SignupSessionInfo
            try {
                payload =
                    typeof event.data === 'string'
                        ? JSON.parse(event.data)
                        : event.data
            } catch {
                return
            }

            if (payload?.type !== 'WA_EMBEDDED_SIGNUP') return

            if (
                payload.event === 'FINISH' ||
                payload.event === 'FINISH_ONLY_WABA'
            ) {
                sessionInfo.current = {
                    wabaId: payload.data?.waba_id,
                    phoneNumberId: payload.data?.phone_number_id,
                }
                return
            }

            if (payload.event === 'CANCEL') {
                // Cancelar não é erro: o operador fechou no meio. Dizer em que
                // passo parou poupa a pergunta "e agora?".
                const step = payload.data?.current_step
                toast.info(
                    step
                        ? `Conexão cancelada na etapa "${step}".`
                        : 'Conexão cancelada.'
                )
                return
            }

            if (payload.event === 'ERROR') {
                toast.error(
                    payload.data?.error_message || 'A Meta recusou a conexão.'
                )
            }
        }

        window.addEventListener('message', onMessage)
        return () => window.removeEventListener('message', onMessage)
    }, [])

    const handleConnect = () => {
        if (!window.FB || !config?.configId) return

        sessionInfo.current = {}
        setConnecting(true)

        window.FB.login(
            (response: any) => {
                const code = response?.authResponse?.code
                if (!code) {
                    setConnecting(false)
                    // Sem code e sem erro do popup: o usuário fechou a janela.
                    return
                }

                connectWhatsappAccount({
                    code,
                    wabaId: sessionInfo.current.wabaId,
                    phoneNumberId: sessionInfo.current.phoneNumberId,
                })
                    .then(res => {
                        if (!res.success) {
                            toast.error(
                                res.error || 'Falha ao concluir a conexão'
                            )
                            return
                        }
                        if (!res.result.subscribed) {
                            // Conta gravada sem app inscrito parece pronta e não
                            // entrega mensagem nenhuma — o pior estado possível.
                            toast.warning(
                                'Conta conectada, mas o app não foi inscrito. Nenhuma mensagem será recebida até reconectar.'
                            )
                        } else if (res.result.registeredPhoneNumbers?.length) {
                            toast.success(
                                'WhatsApp conectado e número registrado — já pode enviar.'
                            )
                        } else {
                            toast.success('WhatsApp conectado.')
                        }
                        fetchStatus()
                    })
                    .finally(() => setConnecting(false))
            },
            {
                config_id: config.configId,
                // `code` em vez de token: a troca acontece no servidor, e o
                // token da conta do cliente nunca passa pelo navegador.
                response_type: 'code',
                override_default_response_type: true,
                extras: { setup: {} },
            }
        )
    }

    const handleRegister = async (number: WhatsappPhoneNumber, pin: string) => {
        setRegisteringId(number.id)
        try {
            const res = await registerWhatsappPhoneNumber(number.id, pin)
            if (res.success && res.result.success) {
                toast.success(
                    'Número registrado. O envio por ele já está habilitado.'
                )
                fetchStatus()
            } else {
                toast.error(
                    res.result?.error ||
                        res.error ||
                        'Falha ao registrar o número'
                )
            }
        } finally {
            setRegisteringId(null)
        }
    }

    /**
     * Desconectar é obrigação, não conveniência: quem conectou precisa poder
     * sair sem depender do suporte. O servidor remove a inscrição do app na
     * conta da Meta antes de apagar o token.
     */
    const handleDisconnect = async () => {
        setDisconnecting(true)
        try {
            const res = await disconnectWhatsappAccount()
            if (res.success) {
                if (!res.result.unsubscribed) {
                    // Local desfeito, remoto não. Dizer isso é melhor do que um
                    // "pronto" que esconde um app ainda inscrito na conta dele.
                    toast.warning(
                        'Conta desconectada aqui, mas a Meta não confirmou a remoção do app. ' +
                            'Confira em Configurações da empresa no Business Manager.'
                    )
                } else {
                    toast.success('WhatsApp desconectado.')
                }
                setShowDisconnectModal(false)
                fetchStatus()
            } else {
                toast.error(res.error || 'Falha ao desconectar')
            }
        } finally {
            setDisconnecting(false)
        }
    }

    const copy = (value: string) => {
        navigator.clipboard
            .writeText(value)
            .then(() => toast.success('Copiado.'))
            .catch(() => toast.error('Não foi possível copiar.'))
    }

    if (!canRead) {
        return (
            <div className="p-6">
                <div className="alert alert-warning">
                    Você não tem permissão para acessar a conta do WhatsApp.
                </div>
            </div>
        )
    }

    return (
        <div className="p-6 space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold">Conta do WhatsApp</h1>
                    <p className="text-sm text-base-content/70">
                        Conecte a conta oficial do WhatsApp Business da sua
                        empresa para enviar e receber mensagens pelo sistema.
                    </p>
                </div>
                <button
                    type="button"
                    className="btn btn-ghost btn-sm gap-2"
                    onClick={fetchStatus}
                    disabled={loading}
                >
                    <MdRefresh size={18} /> Atualizar
                </button>
            </div>

            {loading ? (
                <div className="flex justify-center py-16">
                    <span className="loading loading-spinner loading-lg" />
                </div>
            ) : account?.connected ? (
                <ConnectedCard
                    account={account}
                    canUpdate={canUpdate}
                    canReconnect={canCreate && !!config?.configId && sdkReady}
                    reconnecting={connecting}
                    registeringId={registeringId}
                    onReconnect={handleConnect}
                    onDisconnect={() => setShowDisconnectModal(true)}
                    onRegister={handleRegister}
                    onCopy={copy}
                />
            ) : (
                <NotConnectedCard
                    canCreate={canCreate}
                    configMissing={!config?.configId}
                    sdkReady={sdkReady}
                    sdkFailed={sdkFailed}
                    connecting={connecting}
                    onConnect={handleConnect}
                />
            )}

            <ConfirmModal
                isOpen={showDisconnectModal}
                onClose={() => setShowDisconnectModal(false)}
                onConfirm={handleDisconnect}
                loading={disconnecting}
                title="Desconectar o WhatsApp"
                message={
                    'O sistema deixa de enviar e receber mensagens por esta conta, e o ' +
                    'aplicativo é removido da sua conta do WhatsApp Business na Meta. ' +
                    'Os atendimentos já registrados continuam no histórico. Você pode ' +
                    'conectar de novo quando quiser.'
                }
                confirmButtonText="Desconectar"
            />
        </div>
    )
}

function NotConnectedCard({
    canCreate,
    configMissing,
    sdkReady,
    sdkFailed,
    connecting,
    onConnect,
}: {
    canCreate: boolean
    configMissing: boolean
    sdkReady: boolean
    sdkFailed: boolean
    connecting: boolean
    onConnect: () => void
}) {
    return (
        <div className="card bg-base-100 border border-base-300">
            <div className="card-body items-center text-center gap-4 py-12">
                <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-success/10 text-success">
                    <MdWhatsapp size={36} />
                </span>
                <div className="max-w-lg">
                    <h2 className="text-lg font-semibold">
                        Nenhuma conta conectada
                    </h2>
                    <p className="text-sm text-base-content/70 mt-1">
                        Você será levado a uma janela do Facebook para escolher
                        (ou criar) a conta do WhatsApp Business e o número. Nada
                        é enviado aos seus contatos durante a conexão.
                    </p>
                </div>

                {configMissing ? (
                    <div className="alert alert-warning max-w-lg text-left">
                        <MdErrorOutline size={20} />
                        <span className="text-sm">
                            A conexão ainda não foi habilitada nesta instalação.
                            Fale com o suporte.
                        </span>
                    </div>
                ) : sdkFailed ? (
                    <div className="alert alert-error max-w-lg text-left">
                        <MdErrorOutline size={20} />
                        <span className="text-sm">
                            Não foi possível carregar a janela do Facebook.
                            Bloqueadores de anúncio costumam impedir — desative
                            para este site e recarregue.
                        </span>
                    </div>
                ) : (
                    <button
                        type="button"
                        className="btn btn-success gap-2"
                        onClick={onConnect}
                        disabled={!canCreate || !sdkReady || connecting}
                    >
                        {connecting ? (
                            <span className="loading loading-spinner loading-sm" />
                        ) : (
                            <MdLink size={18} />
                        )}
                        Conectar WhatsApp
                    </button>
                )}

                {!canCreate && (
                    <p className="text-xs text-base-content/50">
                        Você não tem permissão para conectar a conta.
                    </p>
                )}
            </div>
        </div>
    )
}

/**
 * Registro do número: o PIN é pedido ali mesmo, na linha da tabela.
 *
 * Não é `window.prompt` porque o PIN não é um detalhe de formulário — ele vira
 * a verificação em duas etapas DESTE número e passa a ser exigido para
 * registrá-lo em qualquer aplicativo daí em diante. Isso precisa estar escrito
 * na tela, e um prompt do navegador não comporta a explicação nem permite
 * validar os 6 dígitos antes de enviar.
 */
function RegisterCell({
    number,
    registering,
    onRegister,
}: {
    number: WhatsappPhoneNumber
    registering: boolean
    onRegister: (number: WhatsappPhoneNumber, pin: string) => void
}) {
    const [open, setOpen] = useState(false)
    const [pin, setPin] = useState('')

    const valid = /^\d{6}$/.test(pin)

    const submit = () => {
        if (!valid || registering) return
        onRegister(number, pin)
    }

    if (!open) {
        return (
            <button
                type="button"
                className="btn btn-outline btn-xs"
                onClick={() => setOpen(true)}
                title="Habilita o envio por este número"
            >
                Registrar
            </button>
        )
    }

    return (
        <div className="flex flex-col items-end gap-1">
            <div className="flex items-center justify-end gap-2">
                <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    maxLength={6}
                    value={pin}
                    autoFocus
                    disabled={registering}
                    onChange={e => setPin(e.target.value.replace(/\D/g, ''))}
                    onKeyDown={e => {
                        if (e.key === 'Enter') submit()
                        if (e.key === 'Escape') {
                            setOpen(false)
                            setPin('')
                        }
                    }}
                    placeholder="PIN de 6 dígitos"
                    className="input input-bordered input-xs w-36 text-center tracking-widest"
                />
                <button
                    type="button"
                    className="btn btn-primary btn-xs"
                    onClick={submit}
                    disabled={!valid || registering}
                >
                    {registering ? (
                        <span className="loading loading-spinner loading-xs" />
                    ) : (
                        'Enviar'
                    )}
                </button>
            </div>
            <span className="text-[11px] leading-tight text-base-content/50 text-right max-w-[15rem]">
                Escolha um código e guarde: ele vira a verificação em duas
                etapas deste número.
            </span>
        </div>
    )
}

function ConnectedCard({
    account,
    canUpdate,
    canReconnect,
    reconnecting,
    registeringId,
    onReconnect,
    onDisconnect,
    onRegister,
    onCopy,
}: {
    account: WhatsappAccountStatusResult
    canUpdate: boolean
    canReconnect: boolean
    reconnecting: boolean
    registeringId: string | null
    onReconnect: () => void
    onDisconnect: () => void
    onRegister: (number: WhatsappPhoneNumber, pin: string) => void
    onCopy: (value: string) => void
}) {
    const numbers = account.phoneNumbers ?? []
    const pins = account.phonePins ?? {}

    return (
        <div className="space-y-6">
            <div className="card bg-base-100 border border-base-300">
                <div className="card-body gap-4">
                    <div className="flex items-start justify-between flex-wrap gap-3">
                        <div className="flex items-center gap-3">
                            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-success/10 text-success">
                                <MdCheckCircle size={26} />
                            </span>
                            <div>
                                <h2 className="font-semibold">
                                    Conta conectada
                                </h2>
                                <p className="text-sm text-base-content/60">
                                    ID da conta:{' '}
                                    <span className="font-mono">
                                        {account.wabaId}
                                    </span>
                                    <button
                                        type="button"
                                        className="btn btn-ghost btn-xs ml-1"
                                        onClick={() =>
                                            onCopy(account.wabaId ?? '')
                                        }
                                        title="Copiar o ID da conta"
                                    >
                                        <MdContentCopy size={14} />
                                    </button>
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <span
                                className={`badge ${
                                    account.status === 'ACTIVE'
                                        ? 'badge-success'
                                        : 'badge-error'
                                }`}
                            >
                                {account.status}
                            </span>
                            {/* Reconectar existe porque o acesso pode morrer do
                                lado de lá: o cliente revoga o app no Business
                                Manager dele. Sem este botão a tela fica sem
                                saída, e a única alternativa seria o suporte
                                colar um token à mão. Trocar de conta NÃO passa
                                por aqui: o servidor recusa outra WABA. */}
                            <button
                                type="button"
                                className="btn btn-ghost btn-sm gap-2"
                                onClick={onReconnect}
                                disabled={!canReconnect || reconnecting}
                                title="Refaz a conexão com a Meta na mesma conta. Para trocar de conta, desconecte a atual antes."
                            >
                                {reconnecting ? (
                                    <span className="loading loading-spinner loading-xs" />
                                ) : (
                                    <MdSync size={18} />
                                )}
                                Reconectar
                            </button>
                            {/* Sem saída não passa: a Meta exige que o cliente
                                possa remover o app da conta dele por onde o
                                conectou, sem depender de suporte. */}
                            <button
                                type="button"
                                className="btn btn-ghost btn-sm gap-2 text-error"
                                onClick={onDisconnect}
                                title="Remove o aplicativo da sua conta na Meta e apaga as credenciais"
                            >
                                <MdLinkOff size={18} />
                                Desconectar
                            </button>
                        </div>
                    </div>

                    {account.lastError && (
                        <div className="alert alert-error text-sm">
                            <MdErrorOutline size={18} />
                            <span>{account.lastError}</span>
                        </div>
                    )}
                </div>
            </div>

            <div className="card bg-base-100 border border-base-300">
                <div className="card-body gap-3">
                    <div className="flex items-start justify-between flex-wrap gap-3">
                        <div className="max-w-2xl">
                            <h3 className="font-semibold">Números</h3>
                        </div>
                        {/* Mesmo fluxo do Reconectar, com o nome do que a pessoa quer
                            fazer: no popup, escolha esta mesma conta e "Insira um novo
                            número". Sem este botão, adicionar número parecia exigir
                            desconectar a conta. */}
                        <button
                            type="button"
                            className="btn btn-outline btn-sm gap-2 shrink-0"
                            onClick={onReconnect}
                            disabled={!canReconnect || reconnecting}
                            title="Abre a conexão com a Meta. Escolha esta mesma conta e informe o número novo."
                        >
                            {reconnecting ? (
                                <span className="loading loading-spinner loading-xs" />
                            ) : (
                                <MdAdd size={18} />
                            )}
                            Adicionar número
                        </button>
                    </div>

                    {numbers.length === 0 ? (
                        <div className="rounded-lg border border-dashed border-base-300 px-4 py-6 text-center">
                            <p className="text-sm text-base-content/60">
                                Nenhum número nesta conta ainda.
                            </p>
                            <p className="text-xs text-base-content/50 mt-1">
                                Use "Adicionar número" e, na janela da Meta,
                                escolha a conta{' '}
                                <span className="font-mono">
                                    {account.wabaId}
                                </span>
                                .
                            </p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="table table-sm">
                                <thead>
                                    <tr>
                                        <th>Número</th>
                                        <th>Nome exibido</th>
                                        <th>Qualidade</th>
                                        <th>Situação</th>
                                        <th>PIN de segurança</th>
                                        <th />
                                    </tr>
                                </thead>
                                <tbody>
                                    {numbers.map(number => (
                                        <tr key={number.id}>
                                            <td className="font-mono whitespace-nowrap">
                                                {number.displayPhoneNumber}
                                            </td>
                                            <td>
                                                {number.verifiedName ?? '—'}
                                            </td>
                                            <td>
                                                {number.qualityRating ? (
                                                    <span
                                                        className={`badge badge-sm ${
                                                            QUALITY_COLORS[
                                                                number
                                                                    .qualityRating
                                                            ] ?? 'badge-ghost'
                                                        }`}
                                                    >
                                                        {number.qualityRating}
                                                    </span>
                                                ) : (
                                                    '—'
                                                )}
                                            </td>
                                            <td>
                                                <StatusBadge number={number} />
                                            </td>
                                            <td>
                                                {pins[number.id] ? (
                                                    <button
                                                        type="button"
                                                        className="btn btn-ghost btn-xs gap-1 font-mono tracking-widest"
                                                        onClick={() =>
                                                            onCopy(
                                                                pins[number.id]
                                                            )
                                                        }
                                                        title="Verificação em duas etapas deste número na Meta. Guarde: é ele que permite levar o número para outro fornecedor."
                                                    >
                                                        {pins[number.id]}
                                                        <MdContentCopy
                                                            size={13}
                                                        />
                                                    </button>
                                                ) : (
                                                    <span className="text-base-content/40">
                                                        —
                                                    </span>
                                                )}
                                            </td>
                                            <td className="text-right">
                                                {number.status !==
                                                    'CONNECTED' &&
                                                    canUpdate && (
                                                        <RegisterCell
                                                            number={number}
                                                            registering={
                                                                registeringId ===
                                                                number.id
                                                            }
                                                            onRegister={
                                                                onRegister
                                                            }
                                                        />
                                                    )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default WhatsappAccountPage
